import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ToastContainer } from "../components/ui/toast";
import { Header } from "../components/Header";
import { SEO } from "../components/SEO";
import { FileText, CheckCircle, XCircle } from "lucide-react";
import logoIcon from "../assets/icone.png";
import { useLanguage } from "../hooks/useLanguage";

// CPF Validation
function validateCPF(cpf: string): boolean {
    const cleanCPF = cpf.replace(/\D/g, '');

    if (cleanCPF.length !== 11 || /^(\d)\1+$/.test(cleanCPF)) {
        return false;
    }

    const calculateDigit = (digits: number[]): number => {
        const sum = digits.reduce((acc, digit, idx) => acc + digit * (digits.length + 1 - idx), 0);
        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
    };

    const digits = cleanCPF.split('').map(Number);
    const digit1 = calculateDigit(digits.slice(0, 9));
    const digit2 = calculateDigit(digits.slice(0, 10));

    return digits[9] === digit1 && digits[10] === digit2;
}

// CNPJ Validation
function validateCNPJ(cnpj: string): boolean {
    const cleanCNPJ = cnpj.replace(/\D/g, '');

    if (cleanCNPJ.length !== 14 || /^(\d)\1+$/.test(cleanCNPJ)) {
        return false;
    }

    const calculateDigit = (digits: number[], weights: number[]): number => {
        const sum = digits.reduce((acc, digit, idx) => acc + digit * (weights[idx] ?? 0), 0);
        const remainder = sum % 11;
        return remainder < 2 ? 0 : 11 - remainder;
    };

    const digits = cleanCNPJ.split('').map(Number);
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const digit1 = calculateDigit(digits.slice(0, 12), weights1);
    const digit2 = calculateDigit(digits.slice(0, 13), weights2);

    return digits[12] === digit1 && digits[13] === digit2;
}

export function DocumentValidator() {
    const [inputValue, setInputValue] = useState('');
    const [validationResult, setValidationResult] = useState<{ isValid: boolean; type: 'CPF' | 'CNPJ' | null } | null>(null);
    const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: "success" | "error" | "info" }>>([]);
    const { t } = useLanguage();

    const addToast = (message: string, type: "success" | "error" | "info" = "info") => {
        const id = Date.now().toString();
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const handleValidate = () => {
        const cleanInput = inputValue.replace(/\D/g, '');

        if (cleanInput.length === 11) {
            const isValid = validateCPF(inputValue);
            setValidationResult({ isValid, type: 'CPF' });
        } else if (cleanInput.length === 14) {
            const isValid = validateCNPJ(inputValue);
            setValidationResult({ isValid, type: 'CNPJ' });
        } else {
            setValidationResult({ isValid: false, type: null });
        }
    };

    const handleClear = () => {
        setInputValue('');
        setValidationResult(null);
    };

    return (
        <div className="min-h-screen bg-white selection:bg-indigo-100 selection:text-indigo-900 font-sans flex flex-col">
            <SEO
                title="Validar CPF e CNPJ | Hub Links"
                description="Valide CPF e CNPJ gratuitamente. Verifique se documentos brasileiros são válidos online."
                keywords="validar cpf, validar cnpj, cpf válido, cnpj válido, validador de cpf, validador de cnpj, cpf online, cnpj online"
                ogImage="https://hublinks.click/og-documents.jpg"
                ogType="website"
                canonicalUrl="https://hublinks.click/validate-cpf-cnpj"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "WebApplication",
                    "name": "Validador de CPF e CNPJ - Hub Links",
                    "url": "https://hublinks.click/validate-cpf-cnpj",
                    "description": "Valide CPF e CNPJ gratuitamente",
                    "applicationCategory": "UtilitiesApplication",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "BRL"
                    }
                }}
            />
            {/* Hero Section with Gradient Background */}
            <div className="relative overflow-hidden flex-1">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>

                <Header
                    onLoginClick={() => window.location.href = '/login'}
                    onRegisterClick={() => window.location.href = '/register'}
                />

                {/* Hero Content */}
                <main>
                    <div className="max-w-4xl mx-auto text-center space-y-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0DAFFD]/10 border border-[#0DAFFD]/20 text-[#0DAFFD] text-xs font-semibold uppercase tracking-wider shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <FileText className="w-3 h-3" aria-hidden="true" />
                            {t.documentValidator.hero.badge}
                        </div>

                        <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.1] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
                            {t.documentValidator.hero.title} <br className="hidden md:block" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0DAFFD] via-[#0FD8FE] to-[#313131]">
                                {t.documentValidator.hero.titleHighlight}
                            </span>
                        </h1>

                        <p className="text-lg text-slate-600 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-150">
                            {t.documentValidator.hero.description}
                        </p>

                        <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                            <div className="space-y-6">
                                {/* Input */}
                                <div className="space-y-2">
                                    <Input
                                        type="text"
                                        placeholder={t.documentValidator.input.placeholder}
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        className="h-14 text-center text-lg font-mono tracking-wider"
                                        maxLength={18}
                                    />
                                </div>

                                {/* Buttons */}
                                <div className="grid grid-cols-2 gap-4">
                                    <Button
                                        onClick={handleValidate}
                                        disabled={!inputValue.trim()}
                                        className="h-14 bg-gradient-to-r from-[#0DAFFD] to-[#0FD8FE] hover:from-[#0DAFFD] hover:to-[#0FD8FE] text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:shadow-[#0DAFFD]/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {t.documentValidator.buttons.validate}
                                    </Button>

                                    <Button
                                        onClick={handleClear}
                                        variant="secondary"
                                        className="h-14 rounded-xl font-semibold transition-all hover:shadow-md"
                                    >
                                        {t.documentValidator.buttons.clear}
                                    </Button>
                                </div>

                                {/* Result Display */}
                                {validationResult && (
                                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                        <div className="w-full bg-white p-6 rounded-2xl border-2 border-slate-100 shadow-sm">
                                            <div className="space-y-3">
                                                <p className="text-sm font-medium text-slate-600">
                                                    {t.documentValidator.result.title}
                                                </p>

                                                <div className="flex items-center justify-center gap-4 p-4 bg-slate-50 rounded-xl">
                                                    <div className={`flex items-center gap-2 ${validationResult.isValid ? 'text-green-600' : 'text-red-600'}`}>
                                                        {validationResult.isValid ? (
                                                            <CheckCircle className="w-6 h-6" />
                                                        ) : (
                                                            <XCircle className="w-6 h-6" />
                                                        )}
                                                        <span className="text-lg font-semibold">
                                                            {validationResult.isValid ? t.documentValidator.result.valid : t.documentValidator.result.invalid}
                                                        </span>
                                                    </div>
                                                </div>

                                                {validationResult.type && (
                                                    <p className="text-sm text-slate-500 text-center">
                                                        {t.documentValidator.result.typeLabel} {validationResult.type}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-100 pt-16 pb-8 mt-auto" role="contentinfo">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="flex items-center gap-2 font-bold text-lg text-slate-900">
                            <img src={logoIcon} alt="Hub Links" className="w-6 h-6 rounded-md" />
                            Hub Links
                        </div>
                        <div className="text-sm text-slate-500">
                            © 2025 Hub Links. Todos os direitos reservados.
                        </div>
                        <nav className="flex gap-6" aria-label="Redes sociais">
                            <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="Twitter">Twitter</a>
                            <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="Instagram">Instagram</a>
                            <a href="#" className="text-slate-400 hover:text-indigo-600 transition-colors" aria-label="GitHub">GitHub</a>
                        </nav>
                    </div>
                </div>
            </footer>

            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </div>
    );
}
